// Wait for runtime config to load before bootstrapping the app
// The config promise is set by the inline script in index.html
// Note: Window.__RUNTIME_CONFIG__ type is defined in @gosolo/marketplace-api/src/featureFlags.tsx
declare global {
  interface Window {
    __CONFIG_PROMISE__?: Promise<void>;
  }
}

/**
 * Ensures config is loaded before bootstrapping the app.
 * Handles race conditions and provides fallback behavior.
 */
async function initializeApp(): Promise<void> {
  try {
    // Wait for config promise if it exists
    // The inline script in index.html sets this to a promise that resolves
    // when config.json is loaded (or fails gracefully)
    if (window.__CONFIG_PROMISE__) {
      await window.__CONFIG_PROMISE__;
    }

    // Verify config is available (either from config.js or config.json)
    if (!window.__RUNTIME_CONFIG__) {
      console.warn('[App Init] Runtime config not loaded, using defaults');
      window.__RUNTIME_CONFIG__ = {
        apiUrl: 'http://localhost:8099',
        natsWsUrl: 'ws://localhost:8080',
      };
    }

    // Import and run bootstrap
    await import('./bootstrap');
  } catch (error) {
    // Log full error details to console for debugging (not shown to users)
    console.error('[App Init] Failed to initialize application:', error);

    // Show generic user-friendly error message (no technical details exposed)
    const root = document.getElementById('root');
    if (root) {
      root.innerHTML = `
        <div style="display: flex; flex-direction: column; align-items: center; justify-content: center; height: 100vh; font-family: sans-serif; color: #333;">
          <h1 style="color: #d32f2f;">Application Failed to Load</h1>
          <p>We're having trouble loading the application. Please try refreshing the page.</p>
          <p style="color: #666; font-size: 14px;">If the problem persists, please contact support.</p>
          <button onclick="window.location.reload()" style="margin-top: 16px; padding: 8px 24px; cursor: pointer; background: #1976d2; color: white; border: none; border-radius: 4px;">
            Refresh Page
          </button>
        </div>
      `;
    }
  }
}

// Start the application
initializeApp();

// Empty export to make this file a module for TypeScript
export {};
